import * as React from 'react';
import { connect } from 'react-redux';
import { Trans, withTranslation } from 'react-i18next';
import * as moment from 'moment';
import * as R from 'ramda';

import { Select } from '@common/components/form/inputs/select';
import { pageWrapper, EEventNames } from '../../../../../client/analytics';
import Container from '../../../../common/components/container';
import Overview from '../../../../common/components/overview';
import Bar from '../../../../common/components/bar';
import Spinner from '../../../../common/components/spinner';
import { AreaChart, BarChart } from '../../../../common/components/chart';
import Tooltip from '../../components/tooltip';
import DateRangeSelector, { getDates } from '../../components/date-range-selector';
import { EMetricTypes } from '../../../../common/definitions';
import * as organisationSelector from '../../../organisation/selectors/organisation';

const { Row } = Overview.Content;

const getDateRangeLabel = (fromDate, toDate) => {
  let fromFormat = 'D';
  let toFormat = 'D MMM';

  if (fromDate.month() !== toDate.month()) fromFormat = 'D MMM';
  if (fromDate.year() !== moment().year()) {
    fromFormat = 'D MMM YYYY';
    toFormat = 'D MMM YYYY';
  }

  return `${fromDate.format(fromFormat)} - ${toDate.format(toFormat)}`;
};

class StatisticsCommunicationContainer extends React.Component {
  constructor(props) {
    super();

    this.state = {
      isInitialising: !!props.analytics[EMetricTypes.FEED_MESSAGES_CREATED],
      isFetching: true,
      selectedNetwork: props.match.params.networkId || false,
      selectedOrder: false,
      selectedBars: {
        [EMetricTypes.FEED_MESSAGES_CREATED]: true,
        [EMetricTypes.COMMENTS_CREATED]: true,
        [EMetricTypes.HIGHFIVES_CREATED]: true,
      },
      selectedDateValue: 2,
      dates: getDates(2),
    };

    this.handleSetDateRange = this.handleSetDateRange.bind(this);
    this.handleChangeSelectedNetwork = (option) => this.setState({ selectedNetwork: option.id }, this.handleFetchData);
    this.handleChangeOrder = (option) => this.setState({ selectedOrder: option.value });
    this.handleToggleBar = (dataKey) => this.setState({
      selectedBars: { ...this.state.selectedBars, [dataKey]: !this.state.selectedBars[dataKey] },
    });
  }

  UNSAFE_componentWillMount() {
    this.handleFetchData();
  }

  static props;

  async handleFetchData() {
    const { dates: [current, comparison] } = this.state;

    this.setState({ isFetching: true });

    await Promise.all([
      this.props.fetchAnalytics(
        [
          EMetricTypes.PRIVATE_MESSAGES_CREATED,
          EMetricTypes.FEED_MESSAGES_CREATED,
          EMetricTypes.HIGHFIVES_CREATED,
          EMetricTypes.COMMENTS_CREATED,
        ],
        current.start,
        current.end,
        comparison.start,
        comparison.end,
        this.state.selectedNetwork,
      ),
      this.props.fetchAnalytics(
        [
          EMetricTypes.PRIVATE_MESSAGES_CREATED,
          EMetricTypes.FEED_MESSAGES_CREATED,
          EMetricTypes.HIGHFIVES_CREATED,
          EMetricTypes.COMMENTS_CREATED,
        ],
        current.start,
        current.end,
        comparison.start,
        comparison.end,
        this.state.selectedNetwork,
        'communication.group',
      ),
    ]);

    this.setState({ isInitialising: false, isFetching: false });
  }

  handleSetDateRange({ value, dates }) {
    this.setState({
      selectedDateValue: value,
      dates,
    }, this.handleFetchData);
  }

  render() {
    const {
      isInitialising, isFetching, selectedNetwork, selectedOrder, selectedDateValue, selectedBars,
    } = this.state;
    const { match: { params }, analytics, networks, t } = this.props;

    const getTimeseries = (key) => (analytics.data[key] ? analytics.data[key].timeseries : []);
    const getGroupValues = (key) => (analytics.data[key] ? analytics.data[key].values : []);
    const getCount = (key) => (analytics.data[key] ? analytics.data[key].total : 0);
    const getOldCount = (key) => (analytics.data[key] && analytics.data[key].old_total);
    const getUniqueUsers = (key) => (analytics.data[key] ? analytics.data[key].unique_users : 0);

    const getValues = (tint) => [{
      dataKey: 'old_count',
      label: t('network:analytics_past_period'),
    }, {
      dataKey: 'current_count',
      label: t('network:analytics_selected_period'),
      stroke: tint,
      meta: this.state.dates && getDateRangeLabel(this.state.dates[0].start, this.state.dates[0].end),
    }];

    const getNetworkNames = (data) => data.map((metric) => ({
      [EMetricTypes.FEED_MESSAGES_CREATED]: metric[EMetricTypes.FEED_MESSAGES_CREATED],
      [EMetricTypes.COMMENTS_CREATED]: metric[EMetricTypes.COMMENTS_CREATED],
      [EMetricTypes.HIGHFIVES_CREATED]: metric[EMetricTypes.HIGHFIVES_CREATED],
      name: (R.find(R.propEq('id', metric.network_id), networks) || { name: '-' }).name,
    }));

    const loading = (
      isInitialising || isFetching || Object.keys(analytics.data).length <= 0
    );

    return (
      <Container name="Statistics">
        <Container.Content>
          <Bar>
            <h2>
              <Trans i18nKey="network:analytics_communication" />
            </h2>
            <div className="pull-right">
              <DateRangeSelector value={selectedDateValue} onChange={this.handleSetDateRange} />
              {!params.networkId && (
                <Select
                  searchable
                  clearable={false}
                  value={selectedNetwork}
                  valueKey="id"
                  labelKey="name"
                  options={[
                    { id: false, name: t('network:analytics_network_selector_all_networks') },
                    ...networks,
                  ]}
                  onChange={this.handleChangeSelectedNetwork}
                />
              )}
            </div>
          </Bar>
          <Overview>
            <Overview.Header>
              <Overview.Header.Title>
                <Trans
                  i18nKey="network:analytics_counts_over_period"
                  tOptions={{ context: selectedNetwork ? 'network' : 'organisation' }}
                />
              </Overview.Header.Title>
            </Overview.Header>
            <Overview.Content>
              {loading ? <Spinner size="large" centered /> : (
                <Row>
                  <Row.StatisticsCard
                    color="#5856D6"
                    title={t('network:charts_count_posts')}
                    tooltip={t('network:counts_posts_tooltip')}
                    count={getCount(EMetricTypes.FEED_MESSAGES_CREATED)}
                    oldCount={getOldCount(EMetricTypes.FEED_MESSAGES_CREATED)}
                    description={t('network:charts_count_by_unique_users', {
                      count: getUniqueUsers(EMetricTypes.FEED_MESSAGES_CREATED),
                    })}
                  />
                  <Row.Divider />
                  <Row.StatisticsCard
                    color="#5AC8FA"
                    title={t('network:charts_count_high_fives')}
                    tooltip={t('network:counts_high_fives_tooltip')}
                    count={getCount(EMetricTypes.HIGHFIVES_CREATED)}
                    oldCount={getOldCount(EMetricTypes.HIGHFIVES_CREATED)}
                    description={t('network:charts_count_by_unique_users', {
                      count: getUniqueUsers(EMetricTypes.HIGHFIVES_CREATED),
                    })}
                  />
                  <Row.Divider />
                  <Row.StatisticsCard
                    color="#19214D"
                    title={t('network:charts_count_comments')}
                    tooltip={t('network:counts_comments_tooltip')}
                    count={getCount(EMetricTypes.COMMENTS_CREATED)}
                    oldCount={getOldCount(EMetricTypes.COMMENTS_CREATED)}
                    description={t('network:charts_count_by_unique_users', {
                      count: getUniqueUsers(EMetricTypes.COMMENTS_CREATED),
                    })}
                  />
                  <Row.Divider />
                  <Row.StatisticsCard
                    color="#4CD964"
                    title={t('network:charts_count_chat_messages')}
                    tooltip={t('network:counts_chat_messages_tooltip')}
                    count={getCount(EMetricTypes.PRIVATE_MESSAGES_CREATED)}
                    oldCount={getOldCount(EMetricTypes.PRIVATE_MESSAGES_CREATED)}
                    description={t('network:charts_count_by_unique_users', {
                      count: getUniqueUsers(EMetricTypes.PRIVATE_MESSAGES_CREATED),
                    })}
                  />
                </Row>
              )}
            </Overview.Content>
          </Overview>
          <Overview>
            <Overview.Header>
              <Overview.Header.Title>
                <Trans i18nKey="network:analytics_communication_in_period" />
              </Overview.Header.Title>
            </Overview.Header>
            <Overview.Content>
              {loading ? <Spinner size="large" centered /> : (
                <>
                  <div className="Row">
                    <div className="Column">
                      <AreaChart
                        data={getTimeseries(EMetricTypes.FEED_MESSAGES_CREATED)}
                        title={t('network:charts_posts_created')}
                        values={getValues('#5856D6')}
                        XAxisDataKey="date"
                        tint="#5856D6"
                        CustomTooltip={<Tooltip tooltipValueLabel={t('network:charts_count_posts')} />}
                      />
                    </div>
                    <div className="Column">
                      <AreaChart
                        data={getTimeseries(EMetricTypes.HIGHFIVES_CREATED)}
                        title={t('network:charts_high_fives_created')}
                        values={getValues('#5AC8FA')}
                        XAxisDataKey="date"
                        tint="#5AC8FA"
                        CustomTooltip={<Tooltip tooltipValueLabel={t('network:charts_count_high_fives')} />}
                      />
                    </div>
                  </div>
                  <div className="Row">
                    <div className="Column">
                      <AreaChart
                        data={getTimeseries(EMetricTypes.COMMENTS_CREATED)}
                        title={t('network:charts_comments_created')}
                        values={getValues('#19214D')}
                        XAxisDataKey="date"
                        tint="#19214D"
                        CustomTooltip={<Tooltip tooltipValueLabel={t('network:charts_count_comments')} />}
                      />
                    </div>
                    <div className="Column">
                      <AreaChart
                        data={getTimeseries(EMetricTypes.PRIVATE_MESSAGES_CREATED)}
                        title={t('network:charts_chat_messages_send')}
                        values={getValues('#4CD964')}
                        XAxisDataKey="date"
                        tint="#4CD964"
                        CustomTooltip={<Tooltip tooltipValueLabel={t('network:charts_count_chat_messages')} />}
                      />
                    </div>
                  </div>
                </>
              )}
            </Overview.Content>
          </Overview>
          <Overview>
            <Overview.Header>
              <Overview.Header.Title>
                <Trans i18nKey="network:analytics_communication_in_period_per_community" />
              </Overview.Header.Title>
              {!selectedNetwork && (
                <div className="pull-right">
                  <Select
                    clearable={false}
                    value={selectedOrder}
                    valueRenderer={({ value, label }) => (
                      value ? t('network:analytics_sort_by', { label: label.toLowerCase() }) : label
                    )}
                    options={[
                      { value: false, label: t('network:analytics_sort_all') },
                      { value: EMetricTypes.FEED_MESSAGES_CREATED, label: t('network:charts_posts_created') },
                      { value: EMetricTypes.COMMENTS_CREATED, label: t('network:charts_comments_created') },
                      { value: EMetricTypes.HIGHFIVES_CREATED, label: t('network:charts_high_fives_created') },
                    ]}
                    onChange={this.handleChangeOrder}
                    style={{ width: 250 }}
                  />
                </div>
              )}
            </Overview.Header>
            <Overview.Content>
              {loading ? <Spinner size="large" centered /> : (
                <BarChart
                  data={getNetworkNames(getGroupValues('communication.group'))}
                  XAxisDataKey="name"
                  orderKey={selectedOrder}
                  values={[{
                    label: t('network:charts_posts_created'),
                    dataKey: EMetricTypes.FEED_MESSAGES_CREATED,
                    fill: '#5856D6',
                    checked: selectedBars[EMetricTypes.FEED_MESSAGES_CREATED],
                  }, {
                    label: t('network:charts_comments_created'),
                    dataKey: EMetricTypes.COMMENTS_CREATED,
                    fill: '#19214D',
                    checked: selectedBars[EMetricTypes.COMMENTS_CREATED],
                  }, {
                    label: t('network:charts_high_fives_created'),
                    dataKey: EMetricTypes.HIGHFIVES_CREATED,
                    fill: '#5AC8FA',
                    checked: selectedBars[EMetricTypes.HIGHFIVES_CREATED],
                  }]}
                  onToggleBar={this.handleToggleBar}
                />
              )}
            </Overview.Content>
          </Overview>
        </Container.Content>
      </Container>
    );
  }
}

const mapStateToProps = (state) => ({
  analytics: state.analytics,
  networks: organisationSelector.networks(state),
  permissions: state.permissions,
});

const mapDispatchToProps = {
  fetchAnalytics: require('../../actions').fetchAnalytics,
};

export default withTranslation()(
  connect(mapStateToProps, mapDispatchToProps)(
    pageWrapper(EEventNames.VISITED_COMMUNICATION_ANALYTICS_PAGE)(StatisticsCommunicationContainer)
  )
);
